import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeftRight } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { fmtMoney } from '../../shared/format'
import { useTransfer } from '../api'
import { toastError, useFinanceLookups } from '../ui'
import { currencySuffix, fmtAmount } from '../fx'
import { amountRule } from '../validation'
import { Footer } from './AccountForms'
import { Field, FormSection } from './FormBits'

export function TransferForm({ fromId, toId, onDone }: { fromId?: string; toId?: string; onDone: () => void }) {
  const { t } = useTranslation()
  const transfer = useTransfer()
  const { accounts } = useFinanceLookups()
  const schema = z
    .object({
      fromId: z.string().min(1, t('finance.accounts.form.accountRequired')),
      toId: z.string().min(1, t('finance.accounts.form.accountRequired')),
      date: z.string().min(1, t('finance.form.dateRequired')),
      amount: amountRule(t('finance.form.amountInvalid')).refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
      note: z.string().trim().max(120),
      commissionRate: z.string().trim(),
      toAmount: z.string().trim(),
    })
    .refine((v) => v.fromId !== v.toId, { path: ['toId'], message: t('errors.TRANSFER_SAME_ACCOUNT') })
    .refine((v) => (byId.get(v.fromId)?.currency ?? 'TRY') === (byId.get(v.toId)?.currency ?? 'TRY') || Number(v.toAmount.replace(',', '.')) > 0, {
      path: ['toAmount'],
      message: t('finance.fx.toAmountRequired'),
    })
    // POS'tan kasa/bankaya aktarımda o anki komisyon oranı zorunlu (0 girilebilir).
    .refine((v) => !isPos(v.fromId) || /^\d{1,2}([.,]\d{1,4})?$/.test(v.commissionRate), {
      path: ['commissionRate'],
      message: t('finance.accounts.pos.rateRequired'),
    })
  const byId = new Map(accounts.map((a) => [a.id, a]))
  function isPos(id: string) {
    return byId.get(id)?.type === 'pos'
  }
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { fromId: fromId ?? '', toId: toId ?? '', date: new Date().toISOString().slice(0, 10), amount: '', note: '', toAmount: '', commissionRate: byId.get(fromId ?? '')?.commissionRate ?? '' },
  })
  const { errors } = form.formState
  const from = byId.get(useWatch({ control: form.control, name: 'fromId' }))
  const to = byId.get(useWatch({ control: form.control, name: 'toId' }))
  const fromCur = from?.currency || 'TRY'
  const toCur = to?.currency || 'TRY'
  const crossCurrency = !!to && fromCur !== toCur
  const toAmount = Number((useWatch({ control: form.control, name: 'toAmount' }) || '0').replace(',', '.'))
  const amount = Number(useWatch({ control: form.control, name: 'amount' }) || 0)
  const rate = Number((useWatch({ control: form.control, name: 'commissionRate' }) || '0').replace(',', '.'))
  const fromPos = from?.type === 'pos'
  const commission = fromPos && amount > 0 ? Math.round(amount * rate) / 100 : 0
  const option = (a: (typeof accounts)[number]) => ({ value: a.id, label: a.name, description: `${t(`finance.accountType.${a.type}`)} · ${fmtAmount(a.balance, a.currency)}` })
  // Kurallar: kredi kartından ve POS'a aktarım yok; POS'tan yalnız kasa/bankaya (komisyonlu tahsilat).
  const fromOptions = accounts.filter((a) => a.active && a.type !== 'credit_card').map(option)
  const toOptions = accounts
    .filter((a) => a.active && a.type !== 'pos' && (!fromPos || a.type === 'cash' || a.type === 'bank'))
    .map(option)

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await transfer.mutateAsync({
        ...input,
        commissionRate: isPos(input.fromId) ? input.commissionRate.replace(',', '.') : undefined,
        toAmount: crossCurrency ? input.toAmount.replace(',', '.') : undefined,
      })
      toast.success(t('finance.accounts.transferred'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'INSUFFICIENT_BALANCE') form.setError('amount', { message: t('errors.INSUFFICIENT_BALANCE') })
      else toastError(e)
    }
  })
  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!transfer.isPending}
      icon={<ArrowLeftRight />}
      title={t('finance.accounts.transfer')}
      description={t('finance.accounts.transferHint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={<Footer pending={transfer.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="tf-from" label={t('finance.accounts.from')} error={errors.fromId?.message}>
          <FormSelect
            id="tf-from"
            control={form.control}
            name="fromId"
            placeholder={t('common.select')}
            options={fromOptions}
            onValueChange={(id) => {
              const next = byId.get(id)
              if (next?.type === 'pos') form.setValue('commissionRate', next.commissionRate)
              const to = byId.get(form.getValues('toId'))
              if (next?.type === 'pos' && to && to.type !== 'cash' && to.type !== 'bank') form.setValue('toId', '')
            }}
          />
        </Field>
        <Field id="tf-to" label={t('finance.accounts.to')} error={errors.toId?.message}>
          <FormSelect id="tf-to" control={form.control} name="toId" placeholder={t('common.select')} options={toOptions} />
        </Field>
        <Field id="tf-amount" label={t('finance.amount')} error={errors.amount?.message}>
          <Controller control={form.control} name="amount" render={({ field }) => <NumberInput id="tf-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix={currencySuffix(fromCur)} aria-invalid={!!errors.amount} />} />
        </Field>
        {crossCurrency ? (
          <Field
            id="tf-to-amount"
            label={t('finance.fx.toAmount', { currency: toCur })}
            error={errors.toAmount?.message}
            hint={amount > 0 && toAmount > 0 ? t('finance.fx.impliedRate', { rate: (fromCur === 'TRY' ? amount / toAmount : toAmount / amount).toFixed(4) }) : t('finance.fx.toAmountHint')}
          >
            <Controller control={form.control} name="toAmount" render={({ field }) => <NumberInput id="tf-to-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix={currencySuffix(toCur)} aria-invalid={!!errors.toAmount} />} />
          </Field>
        ) : null}
        {fromPos ? (
          <>
            <Field id="tf-rate" label={t('finance.accounts.pos.rateNow')} error={errors.commissionRate?.message} hint={t('finance.accounts.pos.rateNowHint')}>
              <Input id="tf-rate" inputMode="decimal" placeholder="1,79" {...form.register('commissionRate')} />
            </Field>
            <div className="bg-muted/30 rounded-lg border p-3 text-sm sm:col-span-2">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">{t('finance.accounts.pos.commission')}</span>
                <span className="tabular-nums">{fmtMoney(commission.toFixed(2))}</span>
              </div>
              <div className="flex justify-between gap-2 font-medium">
                <span>{t('finance.accounts.pos.net')}</span>
                <span className="tabular-nums">{fmtMoney(Math.max(0, amount - commission).toFixed(2))}</span>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">{t('finance.accounts.pos.expenseHint')}</p>
            </div>
          </>
        ) : null}
        <Field id="tf-date" label={t('finance.tx.date')} error={errors.date?.message}>
          <Controller control={form.control} name="date" render={({ field }) => <DateField id="tf-date" value={field.value} onChange={field.onChange} clearable={false} />} />
        </Field>
        <Field id="tf-note" label={t('finance.pay.note')} full>
          <Input id="tf-note" {...form.register('note')} />
        </Field>
      </FormSection>
    </Modal>
  )
}
